/**
 * Belgian pillar-page content — the first 4 of the 8 litigation
 * landing pages from BP §14.2 (contentieux belge).
 *
 * LEGAL-HONESTY RULE (non-negotiable)
 * ───────────────────────────────────
 * No specific delay, fine amount, or law-article number is ever asserted
 * unless certainty is absolute. When there's any doubt, we use a generic
 * phrasing ("dans un délai légal strict", "l'amende peut être majorée")
 * and drop a `// TODO-VERIFY: <what to check>` comment next to the vague
 * phrase. Better vague and true than precise and wrong.
 *
 * Grep for TODO-VERIFY to review every unverified fact before enriching
 * the copy with hard numbers.
 *
 * Related pages (crosslinks used by BePillarPage):
 *   /contester-amende-radar       → /lettre-huissier-que-faire (if the fine
 *                                     escalates to bailiff)
 *   /lettre-huissier-que-faire    ↔ /facture-abusive-contester (a contested
 *                                     invoice can produce a bailiff visit)
 *   /caution-locative-non-rendue  → (standalone)
 *   /facture-abusive-contester    ↔ /lettre-huissier-que-faire
 */

// Rendering note: `sections[].content` is rendered as plain text inside
// a <div>. Line breaks intended for the reader must be encoded as
// `\n\n` (paragraph break) — the template splits on double newlines.

export const BE_PILLAR_CONTENT = {

  // ─────────────────────────────────────────────────────────────────
  // 1. Contester une amende / PV radar
  // ─────────────────────────────────────────────────────────────────
  'contester-amende-radar': {
    slug: 'contester-amende-radar',
    title: 'Contester une amende radar',
    heroTitle: 'PV radar en Belgique : ce qu’il faut vérifier avant de payer',
    heroSubtitle:
      'Tu viens de recevoir un avis pour excès de vitesse. Avant de sortir la carte bleue, prends deux minutes : plusieurs éléments peuvent rendre le PV contestable, et tu as des droits à chaque étape.',
    intro:
      'Recevoir un PV radar, ça pique, surtout quand tu es sûr d’avoir été prudent, ou que la zone n’était pas clairement signalée. Bonne nouvelle : entre le flash et le paiement, il y a de vraies vérifications à faire et plusieurs étapes distinctes. Tu n’es pas obligé de payer sans réfléchir.',

    sections: [
      {
        title: 'Les 3 étapes que tout PV suit en Belgique',
        content:
          'En Belgique, un excès de vitesse peut passer par trois voies successives. La première, c’est la perception immédiate : un montant proposé, à payer rapidement, qui éteint les poursuites si tu paies dans le délai indiqué. La deuxième, si tu ne paies pas ou si l’infraction est plus grave, c’est la transaction pénale proposée par le parquet : un règlement à l’amiable, généralement à un montant plus élevé, qui évite le tribunal. La troisième, en cas de refus ou d’absence de réponse, c’est la citation à comparaître devant le tribunal de police.\n\nÀ chaque étape, tes options changent. Payer une perception immédiate, c’est reconnaître l’infraction. Ignorer une transaction pénale, c’est presque toujours voir arriver une citation, et le tribunal peut ajouter des frais.',
        // TODO-VERIFY: [délais exacts entre perception immédiate → transaction pénale → citation]
        // TODO-VERIFY: [ordre de grandeur des majorations entre chaque étape]
      },

      {
        title: 'Ce qu’il faut vérifier avant de payer',
        content:
          'Trois vérifications valent la peine avant d’envoyer l’argent.\n\n1) La signalisation. La limitation de vitesse doit être signalée conformément à la réglementation. Un panneau manquant, mal placé, masqué par la végétation, ou une zone de fin de limitation non signalée : ce sont des éléments concrets à documenter, avec des photos si possible.\n\n2) La marge technique. Les radars ne sont pas des instruments parfaits ; le droit prévoit une marge technique déduite de la vitesse mesurée. Si ta vitesse enregistrée est très proche de la limite, cette marge peut faire basculer l’infraction.\n\n3) L’homologation et l’étalonnage. L’appareil doit être homologué et étalonné dans les délais réglementaires. Tu peux demander la copie du procès-verbal d’étalonnage et du certificat d’homologation : c’est un droit du destinataire du PV.',
        // TODO-VERIFY: [valeur exacte de la marge technique par type d'appareil (fixe, mobile, cinémomètre)]
        // TODO-VERIFY: [périodicité obligatoire d'étalonnage]
      },

      {
        title: 'Contester ou payer : comment choisir',
        content:
          'La règle de bon sens : plus l’excès est faible et plus les conditions du contrôle sont douteuses, plus il vaut la peine de contester. Plus l’excès est important, plus la contestation demande un dossier solide.\n\nContester coûte peu si tu le fais tôt et par écrit (courrier recommandé), mais elle prend du temps. Payer est rapide et met le dossier derrière toi. À toi de peser : si tu as un doute réel sur la signalisation ou l’appareil, ne paie pas. Écris. Si tu sais que tu roulais nettement au-dessus, la contestation a peu de chances d’aboutir.',
      },

      {
        title: 'Si l’affaire est déjà chez l’huissier',
        content:
          'Un PV impayé qui a fait l’objet d’un jugement peut être confié à un huissier. Ce n’est plus la même étape : les frais s’ajoutent, et ignorer devient dangereux. Notre guide dédié détaille tes droits face à un huissier. Commence par là.\n\nVoir aussi : /lettre-huissier-que-faire',
      },

      {
        title: 'Ce qu’Archer fait pour toi',
        content:
          'Tu peux téléverser ton PV sur Archer : le scan est gratuit. Archer identifie le type de document (perception immédiate / transaction / citation), lit les dates et les montants, et te donne un Score qui indique si les éléments pour contester tiennent la route.\n\nÀ partir du plan Starter, Archer rédige la lettre de contestation et l’envoie pour toi, en ton nom (en recommandé électronique si tu choisis le supplément). À partir du plan Pro, un juriste prend ton dossier en charge (revue complète, appel, négociation avec le parquet si nécessaire).\n\nCe qu’Archer ne fait pas : promettre une issue. Une contestation dépend des faits, des preuves et de la juridiction. Nous te donnons les outils et le suivi ; le juge décide.',
      },
    ],

    faqs: [
      {
        question: 'Est-ce que payer la perception immédiate empêche toute contestation ?',
        answer:
          'En général, oui : payer vaut reconnaissance de l’infraction. Si tu veux contester, ne paie pas. Envoie ta contestation par écrit dans le délai indiqué sur le document.',
        // TODO-VERIFY: [confirmer que "payer = reconnaître" est vrai pour toutes les perceptions immédiates BE]
      },
      {
        question: 'La marge technique est-elle appliquée automatiquement ?',
        answer:
          'Oui, elle est intégrée au calcul par les autorités. Ce qui peut se vérifier, c’est le montant exact de la marge appliquée à l’appareil qui t’a flashé et la vitesse enregistrée avant marge.',
      },
      {
        question: 'Comment obtenir le certificat d’étalonnage du radar ?',
        answer:
          'En écrivant au parquet ou à l’autorité qui a dressé le PV, avec ta référence de dossier. Tu as le droit d’en recevoir une copie ; c’est une pièce utile si tu contestes sur ce motif.',
      },
      {
        question: 'Si je conteste et que je perds, est-ce que ça coûte plus cher ?',
        answer:
          'Si l’affaire va jusqu’au tribunal et que tu perds, des frais de justice peuvent s’ajouter à l’amende. C’est pour ça que la contestation doit reposer sur des éléments concrets, pas sur "j’ai pas envie de payer".',
        // TODO-VERIFY: [ordre de grandeur des frais de justice tribunal de police]
      },
      {
        question: 'Que se passe-t-il si je ne réponds à rien ?',
        answer:
          'La procédure suit son cours sans toi : transaction pénale, puis citation, puis jugement par défaut. À chaque étape, les frais s’ajoutent. Ne pas répondre est la pire des options.',
      },
    ],

    ctaHeadline: 'Scanne ton PV · c’est gratuit',
    ctaBody:
      'Archer lit ton document, identifie l’étape et te dit ce qui peut être contesté. Aucune carte bancaire pour le scan.',
  },

  // ─────────────────────────────────────────────────────────────────
  // 2. Lettre / visite d'huissier — que faire
  // ─────────────────────────────────────────────────────────────────
  'lettre-huissier-que-faire': {
    slug: 'lettre-huissier-que-faire',
    title: 'Lettre d’huissier : que faire',
    heroTitle: 'Courrier ou visite d’huissier : ce que ça veut dire, ce que tu peux faire',
    heroSubtitle:
      'Un huissier n’est pas un policier. Il ne peut pas tout, et tu as des droits à chaque étape. Ce qu’il faut, c’est comprendre exactement ce qu’il t’adresse, et surtout, ne pas ignorer.',
    intro:
      'Une enveloppe d’huissier dans la boîte, c’est un choc, même quand on n’a rien à se reprocher. Prends une respiration. Un huissier de justice est un officier ministériel qui exécute des actes de procédure, pas un collecteur agressif : il applique une procédure encadrée par la loi, et cette procédure te donne des droits.',

    sections: [
      {
        title: 'Trois types de documents, trois niveaux de gravité',
        content:
          'La mise en demeure : c’est un courrier formel qui te demande de payer ou d’agir dans un délai indiqué. Elle peut venir d’un créancier directement ou par l’intermédiaire d’un huissier, mais elle ne vaut pas encore décision de justice.\n\nLa signification : c’est la remise officielle d’un acte de procédure (une citation, un jugement). L’huissier note la date de remise ; à partir de là, des délais courent contre toi. C’est un point de bascule sérieux.\n\nLa saisie : c’est l’exécution d’un jugement : l’huissier vient constater les biens ou bloquer des sommes. Elle suppose qu’un juge a déjà statué en ta défaveur. À ce stade, ignorer devient dangereux.',
        // TODO-VERIFY: [distinguer précisément saisie conservatoire vs saisie exécutoire vs saisie-arrêt sur salaire, avec les délais associés]
      },

      {
        title: 'Ce qu’un huissier peut faire, et ce qu’il ne peut pas',
        content:
          'Peut : sonner à ta porte, te remettre un acte, te faire signer un procès-verbal de remise, dresser un inventaire des biens visibles si un jugement l’y autorise, opérer des saisies sur salaire ou sur compte bancaire après jugement.\n\nNe peut pas : entrer chez toi sans ton accord ou sans une autorisation légale spécifique, saisir des biens indispensables à la vie quotidienne, exercer des menaces ou une pression déplacée, réclamer des sommes qui ne correspondent pas à un titre exécutoire (jugement, acte notarié, etc.).\n\nÀ chaque intervention, tu as droit à une copie des actes et à savoir précisément d’où vient la créance.',
        // TODO-VERIFY: [liste exacte des biens insaisissables en droit belge (Code judiciaire)]
      },

      {
        title: 'Les 5 réflexes à avoir',
        content:
          '1) Ne jette pas et ne cache pas. Ouvre l’enveloppe, note la date de réception (importante pour les délais), et garde tout.\n\n2) Identifie l’émetteur et la nature. Est-ce une mise en demeure, une signification, une saisie ? Le document le dit : cherche les mots.\n\n3) Vérifie la créance. À quoi correspond-elle exactement ? Est-elle prescrite ? As-tu déjà payé ? Une créance contestée doit être contestée par écrit.\n\n4) Réponds dans les temps. Ignorer, c’est laisser la procédure avancer sans toi. Un écrit, même court, préserve tes droits.\n\n5) Fais analyser. Si tu ne comprends pas de quoi il retourne, un scan Archer (gratuit) identifie le document, sa gravité, et ce qu’il faut faire dans les jours qui suivent.',
      },

      {
        title: 'Si la dette n’est pas la tienne, ou est prescrite',
        content:
          'Il arrive qu’un huissier réclame une dette qui ne t’appartient pas (erreur d’identité, homonymie, dette d’un ex-conjoint) ou qui est prescrite. Dans ces cas, il faut réagir vite et par écrit : un huissier n’a pas le pouvoir de trancher, mais un silence peut te coûter cher plus tard.\n\nUn courrier de contestation motivé, envoyé en recommandé, protège tes droits. Si la créance vient d’une facture que tu contestes déjà, notre guide facture abusive te donne l’angle : /facture-abusive-contester.',
        // TODO-VERIFY: [délais de prescription civile commune en Belgique + prescription des créances entre professionnels]
      },

      {
        title: 'Ce qu’Archer fait pour toi',
        content:
          'Tu peux scanner tout courrier d’huissier sur Archer, gratuitement. Archer identifie le type d’acte (mise en demeure / signification / saisie), lit les dates et le montant réclamé, et te donne un Score qui reflète le degré d’urgence.\n\nÀ partir du plan Starter, Archer rédige et envoie pour toi une réponse motivée : pour contester, demander des précisions, ou proposer un plan d’apurement. En recommandé électronique si tu choisis le supplément.\n\nÀ partir du plan Pro, un juriste prend ton dossier : il analyse le titre exécutoire, vérifie la prescription, contacte l’huissier si nécessaire, et t’accompagne jusqu’à résolution. Archer ne remplace pas l’avocat pour une saisie déjà exécutoire. Dans ce cas, la carte Avocats partenaires prend le relais.',
      },
    ],

    faqs: [
      {
        question: 'L’huissier peut-il entrer chez moi sans mon accord ?',
        answer:
          'En principe non : il faut ton accord ou une autorisation spécifique du juge. La signification d’un acte se fait à la porte ; l’huissier n’entre pas pour te la remettre.',
      },
      {
        question: 'Combien coûte un huissier, et qui paie ?',
        answer:
          'Les frais d’huissier suivent un tarif réglementé et sont généralement à la charge du débiteur, en plus de la somme réclamée. Ces frais s’ajoutent à chaque acte, ce qui rend l’ignorance particulièrement coûteuse.',
        // TODO-VERIFY: [ordre de grandeur des frais d'un acte d'huissier + qui les avance]
      },
      {
        question: 'Puis-je négocier un plan de paiement avec l’huissier ?',
        answer:
          'Oui, souvent. L’huissier peut transmettre au créancier une proposition d’étalement ; c’est parfois accepté, surtout si tu proposes quelque chose de tenable et par écrit.',
      },
      {
        question: 'Un huissier peut-il saisir mon salaire ?',
        answer:
          'Après jugement, oui : une saisie-arrêt sur salaire est possible, dans les limites prévues par la loi (une part de ton revenu reste insaisissable). Cela suppose un titre exécutoire.',
        // TODO-VERIFY: [quotités saisissables sur salaire (barèmes 2026)]
      },
      {
        question: 'Combien de temps ai-je pour réagir à une signification ?',
        answer:
          'Le délai dépend de l’acte signifié : il est indiqué sur le document. Il est en général court, et il court à partir de la date de signification. Ne pas répondre équivaut à laisser la procédure passer.',
      },
    ],

    ctaHeadline: 'Scanne le courrier · sans engagement',
    ctaBody:
      'Archer identifie le document en quelques minutes et te dit ce qui doit se passer dans les prochains jours.',
  },

  // ─────────────────────────────────────────────────────────────────
  // 3. Caution locative non rendue
  // ─────────────────────────────────────────────────────────────────
  'caution-locative-non-rendue': {
    slug: 'caution-locative-non-rendue',
    title: 'Caution locative non rendue',
    heroTitle: 'Le propriétaire garde ta garantie locative : que faire',
    heroSubtitle:
      'Tu as quitté le logement, l’état des lieux est passé, et le compte bloqué ne bouge pas. La libération peut être demandée. Voici comment.',
    intro:
      'Récupérer la garantie locative, c’est souvent la dernière étape d’une location qui se termine mal. La bonne nouvelle : en Belgique, la garantie est bloquée sur un compte spécial au nom du locataire, et sa libération suit des règles claires. Ni le propriétaire ni la banque ne peuvent la libérer unilatéralement contre toi.',

    sections: [
      {
        title: 'Comment fonctionne une garantie locative en Belgique',
        content:
          'La garantie est placée sur un compte bloqué au nom du locataire, dans une banque. Ni le locataire, ni le propriétaire ne peuvent la débloquer seul. La libération se fait de deux manières : par accord écrit des deux parties (le plus simple), ou par une décision du juge de paix (si l’accord est impossible).\n\nLa banque n’est pas juge du désaccord : elle ne libère la garantie que sur présentation soit de l’accord bilatéral, soit d’un jugement. Comprends donc ce que tu vises : obtenir un accord, ou l’arracher via la Justice de paix.',
        // TODO-VERIFY: [nombre de mois de loyer maximum autorisé (2 ou 3) selon type de garantie et région]
      },

      {
        title: 'L’état des lieux : la clé du dossier',
        content:
          'L’état des lieux d’entrée et l’état des lieux de sortie forment la base de tout désaccord sur la garantie. Sans état des lieux d’entrée détaillé et contradictoire, le logement est présumé avoir été loué en bon état, ce qui joue plutôt en ta faveur si le propriétaire veut te reprocher des dégâts.\n\nÀ la sortie, l’état des lieux doit être établi de la même manière : contradictoirement, et idéalement par un expert indépendant si les enjeux sont importants. Refuse de signer un état des lieux avec lequel tu n’es pas d’accord, ou signe en indiquant tes réserves.',
      },

      {
        title: 'La procédure amiable · la première étape',
        content:
          'Commence par un courrier écrit au propriétaire (idéalement en recommandé), qui demande explicitement la libération de la garantie et donne un délai raisonnable de réponse. Ce courrier sert deux fonctions : il fait courir un délai formel et il constitue la preuve que tu as tenté la voie amiable : le juge le vérifiera.\n\nDans le courrier, référence l’état des lieux, la date de fin de bail, et ce que tu contestes s’il te reproche des dégâts. Reste factuel et bref.',
      },

      {
        title: 'Le juge de paix, si l’amiable n’aboutit pas',
        content:
          'Le juge de paix est le tribunal compétent pour les litiges entre bailleur et locataire. La procédure est relativement accessible : pas d’avocat obligatoire pour les cas simples, et les frais restent contenus.\n\nAvant l’audience, une conciliation devant le juge de paix est souvent proposée (ou obligatoire selon les cantons) : c’est une réunion informelle où le juge tente de vous mettre d’accord sans procès. Beaucoup de dossiers se règlent à cette étape.',
        // TODO-VERIFY: [conciliation devant JP obligatoire ou facultative selon type de litige locatif]
      },

      {
        title: 'Ce qu’Archer fait pour toi',
        content:
          'Tu peux scanner ton bail, ton état des lieux et le courrier du propriétaire sur Archer, gratuitement. Archer analyse les documents et te donne un Score sur la solidité de ton dossier (état des lieux valable ? dégâts justifiés ? garantie effectivement bloquée ?).\n\nÀ partir du plan Starter, Archer rédige et envoie pour toi la demande de libération (en recommandé électronique si tu choisis le supplément). Elle mentionne les textes utiles, les faits, et donne un délai précis au propriétaire pour réagir.\n\nÀ partir du plan Pro, un juriste prend ton dossier : il te prépare pour une éventuelle audience de conciliation devant le juge de paix, et t’accompagne dans la procédure si l’amiable échoue.',
      },
    ],

    faqs: [
      {
        question: 'Combien de temps le propriétaire a-t-il pour rendre la garantie ?',
        answer:
          'Il n’y a pas de délai unique : le propriétaire doit se prononcer dans un délai raisonnable après l’état des lieux de sortie. Passé ce délai raisonnable, une mise en demeure écrite est utile pour formaliser ta demande et faire courir un délai plus dur.',
        // TODO-VERIFY: [existence d'un délai légal exprès de libération de garantie]
      },
      {
        question: 'Que faire si le propriétaire réclame plus que la garantie ?',
        answer:
          'Il doit prouver le montant des dégâts (devis, factures, expertise). Sans preuve, sa demande n’est pas fondée. Conteste par écrit et n’accepte pas de retenue non justifiée : la charge de la preuve pèse sur celui qui réclame.',
      },
      {
        question: 'Puis-je récupérer ma garantie sans passer par le juge ?',
        answer:
          'Oui, dans la grande majorité des cas. Un courrier amiable ferme et documenté suffit souvent, surtout si l’état des lieux est propre. Le juge de paix n’intervient qu’en dernier recours.',
      },
      {
        question: 'Est-ce que je peux utiliser la garantie pour payer le dernier mois de loyer ?',
        answer:
          'Non. La garantie n’est pas un dépôt-loyer, c’est une garantie contre d’éventuels dégâts ou impayés. Elle ne se substitue pas au loyer courant. Le faire peut créer un contentieux distinct.',
      },
      {
        question: 'Le propriétaire refuse même de venir à l’état des lieux de sortie. Je fais quoi ?',
        answer:
          'Convoque-le par écrit (recommandé) à une date et une heure précises. S’il ne vient pas, fais établir l’état des lieux par un expert indépendant à tes frais, puis récupère les frais dans la procédure. Documente tout : sans état des lieux de sortie contradictoire, le logement est présumé rendu en bon état.',
      },
    ],

    ctaHeadline: 'Scanne ton bail · c’est gratuit',
    ctaBody:
      'Archer analyse ton bail et ton état des lieux, et te dit si ta demande de libération tient la route.',
  },

  // ─────────────────────────────────────────────────────────────────
  // 4. Facture abusive — contester
  // ─────────────────────────────────────────────────────────────────
  'facture-abusive-contester': {
    slug: 'facture-abusive-contester',
    title: 'Contester une facture abusive',
    heroTitle: 'Facture gonflée ou prestations non demandées : comment contester',
    heroSubtitle:
      'Un fournisseur t’envoie une facture qui ne colle pas avec ce que tu as commandé ? Tu peux, et tu devrais, contester par écrit. Ne pas réagir peut te coûter cher.',
    intro:
      'Une facture qui explose la note, des prestations que tu n’as jamais demandées, un tarif qui ne correspond pas à l’estimation : ça arrive plus souvent qu’on ne le pense, et pas seulement chez les clients particuliers. La règle d’or : réagis vite, par écrit, en gardant une trace. Le silence peut être interprété comme une acceptation tacite, surtout dans un contexte commercial.',

    sections: [
      {
        title: 'La contestation doit être écrite, et rapide',
        content:
          'La forme du courrier compte plus qu’on ne le croit. Une contestation orale ou par simple email peut passer inaperçue. Une contestation écrite, envoyée en recommandé, laisse une trace incontestable de la date et du contenu : c’est ce qui protège tes droits en cas de suite.\n\nLe contenu doit être précis : la date et le numéro de la facture, les postes contestés, la raison de la contestation (prestations non réalisées, tarif non convenu, quantité gonflée), et ce que tu demandes (rectification, avoir, annulation). Reste factuel : pas d’injures, pas de menaces.',
      },

      {
        title: 'La charge de la preuve · un principe à ton avantage',
        content:
          'En droit belge, celui qui réclame le paiement doit prouver qu’il a bien exécuté la prestation et que le montant réclamé est conforme au contrat. Ce principe est puissant : ce n’est pas à toi de prouver que tu n’as rien commandé : c’est au fournisseur de prouver qu’il a bien livré ce qui figure sur la facture.\n\nEn pratique : demande la commande écrite ou le devis signé, le bon de livraison, le détail des heures prestées ou des matériaux. Si le fournisseur ne peut pas produire ces documents, sa facture perd beaucoup de sa force.',
        // TODO-VERIFY: [articles du Code civil / Code de droit économique fondant la charge de la preuve du fournisseur]
      },

      {
        title: 'La "mise en demeure inverse"',
        content:
          'C’est le nom qu’on donne à la contestation formelle où c’est TOI qui mets le fournisseur en demeure : justifier sa facture, la rectifier, ou faire son offre finale. Elle inverse la dynamique : au lieu d’attendre les relances, tu prends la main.\n\nUn tel courrier :\n\n1) rappelle le contrat ou le devis initial,\n2) énumère précisément les postes contestés,\n3) demande les pièces justificatives (bons, feuilles d’heures, plans),\n4) fixe un délai clair de réponse,\n5) annonce, à défaut de réponse ou d’accord, la suite (saisine du médiateur, procédure judiciaire, etc.).',
        // TODO-VERIFY: [médiateurs sectoriels compétents en Belgique (énergie, télécom, banque, consommation)]
      },

      {
        title: 'Attention au silence · surtout en B2B',
        content:
          'En droit commercial, le silence prolongé face à une facture peut être interprété comme une acceptation tacite, en particulier entre professionnels. Ce n’est pas un couperet automatique, mais c’est un facteur que le juge considère.\n\nMorale : ne laisse jamais une facture qui te pose problème sans réponse écrite. Même une contestation minimale, "je conteste, j’attends les justificatifs, on en reparle", vaut mieux qu’un silence qui, plusieurs mois plus tard, devient difficile à défendre.',
        // TODO-VERIFY: [cadre juridique exact du "silence circonstancié" en droit commercial belge (art. code d'act. droit économique)]
      },

      {
        title: 'Si le fournisseur envoie un huissier',
        content:
          'Une facture contestée peut être transmise à un huissier, soit pour une mise en demeure formelle, soit après un jugement. Si tu reçois un courrier d’huissier alors que tu contestes déjà la facture, ne t’affole pas : ta contestation antérieure reste valable et pèse dans le dossier.\n\nRéfère-toi à notre guide dédié aux huissiers : /lettre-huissier-que-faire.',
      },

      {
        title: 'Ce qu’Archer fait pour toi',
        content:
          'Tu peux scanner la facture, le devis initial et les échanges avec le fournisseur sur Archer, gratuitement. Archer identifie les incohérences (postes non prévus, tarifs différents du devis, quantités anormales) et te donne un Score sur la solidité de ta contestation.\n\nÀ partir du plan Starter, Archer rédige la contestation formelle et l’envoie pour toi, avec le bon vocabulaire juridique et les bons délais. En recommandé électronique si tu choisis le supplément.\n\nÀ partir du plan Pro, un juriste prend ton dossier : il analyse le contrat, le devis et la facture, et négocie directement avec le fournisseur si nécessaire. Objectif honnête : trouver un règlement rapide qui tient la route juridiquement.',
      },
    ],

    faqs: [
      {
        question: 'Dans quel délai dois-je contester une facture ?',
        answer:
          'Le plus tôt possible, dès que tu la reçois. En droit commercial, un délai raisonnable est attendu, et un silence prolongé se retourne contre toi. Contester dans les jours qui suivent la réception est la posture la plus sûre.',
        // TODO-VERIFY: [délai raisonnable de contestation d'une facture entre professionnels en droit belge]
      },
      {
        question: 'Un email suffit-il, ou faut-il un recommandé ?',
        answer:
          'Un recommandé fait beaucoup plus de poids. L’email prouve l’envoi, pas nécessairement la réception, et n’a pas la même force en cas de contentieux. Pour une contestation qui compte, le recommandé (électronique ou papier) est la référence.',
      },
      {
        question: 'Le fournisseur refuse de me communiquer le détail de la facture. Que faire ?',
        answer:
          'C’est en soi un motif de contestation. Formalise ta demande par écrit ; l’absence de justificatifs affaiblit sa position. Un juge attendra du fournisseur qu’il prouve ce qu’il facture.',
      },
      {
        question: 'Puis-je refuser de payer tant que la contestation est en cours ?',
        answer:
          'Tu peux retenir le montant contesté, mais paie la partie non contestée si elle existe (par exemple, le montant du devis initial si tu contestes seulement les suppléments). Consigner le montant contesté sur un compte séparé, ou proposer un paiement partiel avec réserves écrites, est une stratégie prudente.',
        // TODO-VERIFY: [exception d'inexécution en droit belge — conditions d'application]
      },
      {
        question: 'Et si la facture vient d’un fournisseur d’énergie ou de télécom ?',
        answer:
          'Les secteurs régulés (énergie, télécom, banque) disposent souvent d’un médiateur sectoriel gratuit, à saisir après une réclamation restée sans réponse chez le fournisseur. C’est une voie efficace avant d’envisager le tribunal.',
      },
    ],

    ctaHeadline: 'Scanne ta facture · c’est gratuit',
    ctaBody:
      'Archer compare ta facture au devis, identifie les postes anormaux et te dit si la contestation tient.',
  },
};

// Slugs listed in the order they appear in the footer "Guides juridiques"
// block and in the sitemap. Kept as a separate export so both surfaces
// stay in sync without duplication.
export const BE_PILLAR_SLUGS = [
  'contester-amende-radar',
  'lettre-huissier-que-faire',
  'caution-locative-non-rendue',
  'facture-abusive-contester',
];
